import { useState } from "react";
import { useToast } from "../../context/ToastContext";
import LabeledBox from "../../components/LabeledBox";
import { ImageUploader } from "../../components/ImageUploader";
import { makeRequest } from "../../makeRequest";
import { useMutation } from "@tanstack/react-query";
import { MY_ENDPOINTS } from "@raashidh/shared";
import { MyInputField } from "../../MY_COMPONENTS/MyInputField";

type ProductForm = {
  product_name: string;
  product_code: string;
  product_description: string;
  category: string;
  brand: string;
  tags: string;
  is_active: boolean;

  variant_name: string;
  sku: string;
  base_price: number;
  stock_quantity: number;
  is_available: boolean;
  is_featured: boolean;
};

interface EditProductFormProps {
  product: ProductForm;
  productId: number;
  variantId: number;
  initialData: any; // product + variant from API
  onSuccess?: () => void;
}

export default function EditProductForm({ product, onSuccess }: EditProductFormProps) {
  const toast = useToast();

  const [productImages, setProductImages] = useState<string[]>(product.product_images || []);
  const [variantImages, setVariantImages] = useState<string[]>(product?.variant.variant_images || []);

  const [form, setForm] = useState<ProductForm>({
    product_name: product.product_name || "",
    product_code: product.product_code || "",
    product_description: product.product_description || "",
    category: product.category || "",
    brand: product.brand || "",
    tags: (product.tags || []).join(","),
    is_active: !!product.is_active,

    variant_name: product?.variant.variant_name || "",
    sku: product?.variant.sku || "",
    base_price: product?.variant.base_price || 0,
    stock_quantity: product?.variant.stock_quantity || 0,
    is_available: !!product?.variant.is_available,
    is_featured: !!product?.variant.is_featured,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        product_id: product?.product_id,
        variant_id: product?.variant?.variant_id,
        product: {
          product_name: form.product_name,
          product_code: form.product_code || null,
          product_description: form.product_description,
          product_images: productImages,
          category: form.category,
          brand: form.brand,
          tags: form.tags.split(",").map((t) => t.trim()),
          is_active: form.is_active ? 1 : 0,
        },
        variant: {
          variant_name: form.variant_name,
          sku: form.sku || null,
          base_price: Number(form.base_price),
          stock_quantity: Number(form.stock_quantity),
          variant_images: variantImages,
          is_available: form.is_available ? 1 : 0,
          is_featured: form.is_featured ? 1 : 0,
        },
      };

      return makeRequest(
        MY_ENDPOINTS.POS_ENDPOINTS.ADMIN_UPDATE_PRODUCT.path.replace(":id", product?.product_id.toString()),
        { method: MY_ENDPOINTS.POS_ENDPOINTS.ADMIN_UPDATE_PRODUCT.method, body: payload }
      );
    },
    onSuccess: () => {
      toast.success("Product updated successfully");
      if (onSuccess) onSuccess();
    },
    onError: (err: any) => toast.error(err.message || "Failed to update product"),
  });

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow p-6 space-y-6">
      {/* PRODUCT IMAGES */}
      <LabeledBox label="Product Images">
        <ImageUploader
          multiple
          onUploadComplete={(urls) => setProductImages((p) => [...p, ...urls])}
          initialImages={productImages}
        />
      </LabeledBox>

      {/* BASIC INFO */}
      <MyInputField label="Product Name" name="product_name" value={form.product_name} onChange={handleChange} />
      <MyInputField label="Product Code" name="product_code" value={form.product_code} onChange={handleChange} />
      <MyInputField label="Description" name="product_description" type="textarea" value={form.product_description} onChange={handleChange} />

      <div className="grid grid-cols-2 gap-4">
        <MyInputField label="Category" name="category" value={form.category} onChange={handleChange} />
        <MyInputField label="Brand" name="brand" value={form.brand} onChange={handleChange} />
      </div>

      <MyInputField label="Tags (comma separated)" name="tags" value={form.tags} onChange={handleChange} />

      {/* VARIANT */}
      <div className="border-t pt-4 space-y-4">
        <h3 className="font-semibold">Default Variant</h3>

        <MyInputField label="Variant Name" name="variant_name" value={form.variant_name} onChange={handleChange} />
        <MyInputField label="SKU" name="sku" value={form.sku} onChange={handleChange} />

        <div className="grid grid-cols-2 gap-4">
          <MyInputField label="Price" name="base_price" value={form.base_price} onChange={handleChange} />
          <MyInputField label="Stock Qty" name="stock_quantity" value={form.stock_quantity} onChange={handleChange} />
        </div>

        <LabeledBox label="Variant Images">
          <ImageUploader
            multiple
            onUploadComplete={(urls) => setVariantImages((p) => [...p, ...urls])}
            initialImages={variantImages}
          />
        </LabeledBox>

        <div className="flex gap-6">
          <label className="flex gap-2 items-center">
            <input type="checkbox" name="is_available" checked={form.is_available} onChange={handleChange} />
            Available
          </label>

          <label className="flex gap-2 items-center">
            <input type="checkbox" name="is_featured" checked={form.is_featured} onChange={handleChange} />
            Featured
          </label>
        </div>
      </div>

      {/* SUBMIT */}
      <button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="w-full bg-black text-white py-3 rounded-lg"
      >
        {mutation.isPending ? "Updating..." : "Update Product"}
      </button>
    </div>
  );
}